export enum AuthErrorCode {
	ACCOUNT_INACTIVE = 'ACCOUNT_INACTIVE',
	/** Token absent, malformed, wrong-signature, or expired — refused 401 before any DB access. */
	TOKEN_REJECTED = 'TOKEN_REJECTED',
	/** Token verifies but no non-deleted TODAY `users` row matches the subject — refused 403. */
	USER_NOT_PROVISIONED = 'USER_NOT_PROVISIONED',
	/** User is inactive/soft-deleted, or the tenant is suspended/deleted — refused 403. */
	PRINCIPAL_INACTIVE = 'PRINCIPAL_INACTIVE',
	/** Cognito rejected the login code exchange, or a returned access/id token failed verification — refused 401. */
	LOGIN_FAILED = 'LOGIN_FAILED',
	/** Refresh cookie absent, or Cognito rejected the refresh token (expired/revoked/malformed) — refused 401. */
	REFRESH_REJECTED = 'REFRESH_REJECTED',
	/**
	 * The access token verified, but was issued before the user's sessions were
	 * revoked (logout or an admin "revoke sessions") — refused 401 (TO-64).
	 */
	SESSION_REVOKED = 'SESSION_REVOKED',
	/** Generic authentication failure (default code of `_401`). */
	UNAUTHORIZED = 'UNAUTHORIZED',
	/**
	 * A landing beacon's tracking token is malformed, forged, expired or missing a
	 * claim (TO-165 REQ-004) — one code and one message for every cause.
	 */
	TRACKING_TOKEN_INVALID = 'TRACKING_TOKEN_INVALID',
	/**
	 * An inbound webhook delivery is not provably authentic (TO-323 REQ-008).
	 *
	 * Deliberately ONE code for every cause — unknown endpoint, suspended
	 * endpoint, dead tenant, provider mismatch, missing or malformed headers,
	 * stale timestamp, wrong digest. Distinguishing them would let an
	 * unauthenticated caller enumerate which endpoint identifiers and providers
	 * exist by reading the difference.
	 */
	WEBHOOK_UNAUTHENTICATED = 'WEBHOOK_UNAUTHENTICATED',
	/**
	 * A request presenting a personal API key was not admitted (TO-516 REQ-008,
	 * REQ-010). Deliberately ONE code and one message for every cause — malformed,
	 * unknown, revoked, expired, idle, inactive owner or tenant, a key alongside a
	 * Bearer token, or a key on an interactive-only route — so a caller cannot
	 * learn anything about a key from the refusal.
	 */
	API_KEY_REJECTED = 'API_KEY_REJECTED',
	/** Authenticated principal lacks the required permission or role (default code of `_403`). */
	FORBIDDEN = 'FORBIDDEN',
	/**
	 * The principal tried to provision into a tenant that is not its own without
	 * the super-admin targeted-tenant bypass — or named a target that is not a
	 * live tenant. Refused 403 (TO-303 REQ-002).
	 */
	CROSS_TENANT_DENIED = 'CROSS_TENANT_DENIED',
	/**
	 * A tenant-scoped admin route's `{slug}` does not name the principal's own
	 * tenant, and the principal holds no cross-tenant capability — or the slug
	 * names no tenant at all (never disclosed). Refused 403 (TO-307 REQ-002).
	 */
	CROSS_TENANT_FORBIDDEN = 'CROSS_TENANT_FORBIDDEN',
	/** The action would grant, strip, or act on a role above the caller's own authority. Refused 403 (TO-307 REQ-006). */
	PRIVILEGE_ESCALATION = 'PRIVILEGE_ESCALATION',
	/** JIT login: the asserted email's domain is not in the tenant's non-empty allowlist — refused 403 (TO-335 REQ-005). */
	JIT_DOMAIN_NOT_ALLOWED = 'JIT_DOMAIN_NOT_ALLOWED',
	/** JIT login: the asserted email already belongs to a user in the target tenant; never auto-linked — refused 403 (TO-335 REQ-008). */
	JIT_EMAIL_CONFLICT = 'JIT_EMAIL_CONFLICT',
	/** Login callback: the principal's tenant has `loginEnabled: false` — refused 403 (TO-410 REQ-009). */
	TENANT_LOGIN_DISABLED = 'TENANT_LOGIN_DISABLED',
	/** Login callback: a native identity while the tenant has `localLoginEnabled: false` — refused 403 (TO-410). */
	LOCAL_LOGIN_DISABLED = 'LOCAL_LOGIN_DISABLED',
	/** Login callback: the IdP org-unit value is unknown and the tenant's policy is `REJECT_LOGIN` — refused 403 (TO-377 REQ-106). */
	ORG_UNIT_UNKNOWN = 'ORG_UNIT_UNKNOWN',
	/** A unit-scoped caller addressed a user they can see but who has a current assignment outside their scope (TO-435 REQ-004). */
	USER_OUT_OF_SCOPE = 'USER_OUT_OF_SCOPE',
	/** A unit-scoped caller tried to place a new user in a unit outside their scope (TO-435 REQ-006). */
	USER_ORG_UNIT_OUT_OF_SCOPE = 'USER_ORG_UNIT_OUT_OF_SCOPE',
	/** Callback/refresh: an MFA-enrolled `ORG_ADMIN`/`PLATFORM_ADMIN` signed in without TOTP — refused 403 (TO-63 REQ-008/009). */
	MFA_REQUIRED = 'MFA_REQUIRED',
	/** Callback: a federated sign-in into a `requireIdpMfa` tenant without an IdP MFA assertion — refused 403 (TO-63 REQ-011). */
	IDP_MFA_REQUIRED = 'IDP_MFA_REQUIRED',
	/** First-login / any surface: the user must enrol a TOTP factor before proceeding — refused 403 (TO-591). */
	MFA_ENROLMENT_REQUIRED = 'MFA_ENROLMENT_REQUIRED',
	/** TOTP enrolment: the enrolment token is not a live enrolment-client access token for the caller — refused 403 (TO-63 REQ-002/005). */
	MFA_ENROLMENT_TOKEN_INVALID = 'MFA_ENROLMENT_TOKEN_INVALID',
	/** TOTP enrolment: Cognito rejected the code, or no association is pending — refused 422 (TO-63 REQ-004). */
	MFA_CODE_INVALID = 'MFA_CODE_INVALID',
	/** TOTP enrolment: Cognito throttled the call — refused 429 (TO-63 REQ-005). */
	MFA_RATE_LIMITED = 'MFA_RATE_LIMITED',
	/** TOTP enrolment: the stage has no enrolment app client configured — 503 (TO-63 REQ-005). */
	MFA_ENROLMENT_UNAVAILABLE = 'MFA_ENROLMENT_UNAVAILABLE',
}

// Moved to sibling files (TO-591), re-exported through the `@today/types` barrel:
//   NotFoundErrorCode, ConflictErrorCode, GoneErrorCode → ./http-error-codes.ts
//   ValidationErrorCode                                  → ./validation-error-codes.ts

/** Codes for 503 responses: an external dependency was unavailable and nothing was persisted. */
export enum ServiceUnavailableErrorCode {
	/** Default: the generic `_503` code. */
	SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
	/** Cognito (the identity provider) was unreachable, or IdP writes are off on this stage (TO-410). */
	IDP_UNAVAILABLE = 'IDP_UNAVAILABLE',
	/** Secrets Manager was unreachable, or secret writes are off on this stage (TO-410 design D7). */
	SECRET_STORE_UNAVAILABLE = 'SECRET_STORE_UNAVAILABLE',
	/** EventBridge rejected an event whose publication IS the operation (TO-375 provisioning retry). */
	EVENT_BUS_UNAVAILABLE = 'EVENT_BUS_UNAVAILABLE',
	/** Login callback: the IdP attribute sync could not be written; the login is refused (TO-377 REQ-108). */
	LOGIN_SYNC_UNAVAILABLE = 'LOGIN_SYNC_UNAVAILABLE',
	/** A Twilio webhook endpoint was requested while the platform Twilio auth token is unset; no row was created (TO-474 REQ-001). */
	WEBHOOK_PROVIDER_UNCONFIGURED = 'WEBHOOK_PROVIDER_UNCONFIGURED',
	/** SES could not register or read the sending-domain identity; nothing was changed (TO-382). */
	SENDER_DOMAIN_PROVIDER_UNAVAILABLE = 'SENDER_DOMAIN_PROVIDER_UNAVAILABLE',
	/** A test SMS: the provider, or the tenant's SMS binding, was unavailable (TO-383). */
	SMS_PROVIDER_UNAVAILABLE = 'SMS_PROVIDER_UNAVAILABLE',
	/** AWS Transfer Family or Secrets Manager failed an SFTP connector call; nothing was stored (TO-393). */
	SFTP_PROVIDER_UNAVAILABLE = 'SFTP_PROVIDER_UNAVAILABLE',
	/** A new user committed but could not be placed in their org unit; they stay unplaced (TO-435 REQ-008). */
	ORG_UNIT_PLACEMENT_UNAVAILABLE = 'ORG_UNIT_PLACEMENT_UNAVAILABLE',
}

/**
 * Machine-readable identity audit signals. Distinct from {@link AuthErrorCode}:
 * these are recorded in logs for an operator/auditor to act on, never returned to
 * a caller as an API error code.
 */
export enum IdentityAuditCode {
	/**
	 * A federated IdP asserted a group marker naming a privileged or foreign-tenant
	 * group; the value was observed, dropped, and the identity left confined to its
	 * own tenant group.
	 */
	IDP_GROUP_MAPPING_REJECTED = 'IDP_GROUP_MAPPING_REJECTED',
	/** A JIT login was refused: the email domain is outside the tenant's allowlist (TO-335 REQ-005). */
	JIT_DOMAIN_NOT_ALLOWED = 'JIT_DOMAIN_NOT_ALLOWED',
	/** A JIT login was refused: the email is already held in the target tenant (TO-335 REQ-008). */
	JIT_EMAIL_CONFLICT = 'JIT_EMAIL_CONFLICT',
	/** A JIT login was refused as not eligible; the caller sees `USER_NOT_PROVISIONED` (TO-335 REQ-006). */
	JIT_NOT_ELIGIBLE = 'JIT_NOT_ELIGIBLE',
	/** A JIT user was created but its `identity.user_jit_provisioned` event failed to publish (TO-335 REQ-010). */
	JIT_AUDIT_EMIT_FAILED = 'JIT_AUDIT_EMIT_FAILED',
	/** A sign-in was refused because the tenant has `loginEnabled: false` (TO-410 REQ-009). */
	TENANT_LOGIN_DISABLED = 'TENANT_LOGIN_DISABLED',
	/** A native-identity sign-in was refused because the tenant has `localLoginEnabled: false` (TO-410 REQ-010). */
	LOCAL_LOGIN_DISABLED = 'LOCAL_LOGIN_DISABLED',
	/** A privileged user confirmed a TOTP factor (TO-63 REQ-004). */
	MFA_ENROLLED = 'MFA_ENROLLED',
	/** A sign-in or refresh was refused: enrolled privileged user without TOTP (TO-63 REQ-008/009). */
	MFA_REQUIRED = 'MFA_REQUIRED',
	/** A federated sign-in was refused: the tenant requires an IdP MFA assertion (TO-63 REQ-011). */
	IDP_MFA_REQUIRED = 'IDP_MFA_REQUIRED',
}

/**
 * The ID-token claim `today_mfa` the pre-token trigger stamps (TO-63 REQ-007):
 * the sign-in's MFA state, which the login callback and refresh enforce.
 */
export enum MfaClaim {
	/** Native identity with a confirmed TOTP factor (Cognito challenged it). */
	TOTP = 'totp',
	/** Native identity without a confirmed TOTP factor. */
	NONE = 'none',
	/** Federated identity whose IdP asserted MFA. */
	IDP = 'idp',
	/** Federated identity whose IdP did not assert MFA. */
	IDP_NONE = 'idp_none',
}

/** The ID-token claim name carrying {@link MfaClaim}. */
export const MFA_CLAIM_NAME = 'today_mfa'

export const ACCOUNT_INACTIVE_MESSAGE = 'Your account is pending activation. Contact an administrator.'

/**
 * The outcome of one request at the Bearer auth boundary, as counted in
 * CloudWatch (TO-311 REQ-301). A closed enumeration, not string literals
 * (CLAUDE.md), so the metric's `Outcome` dimension has fixed cardinality and an
 * unrecognised outcome cannot be emitted.
 *
 * Distinct from {@link AuthErrorCode}: that is what a caller is told, this is
 * what an operator counts. `AMBIGUOUS` resolution is counted as
 * `PRINCIPAL_INACTIVE` because that is the refusal the caller observes — the
 * metric mirrors the response rather than inventing a finer taxonomy.
 */
export enum AuthOutcome {
	/** A DB-resolved principal was attached and every route gate passed. */
	ADMITTED = 'admitted',
	/** The token was absent, malformed, wrongly signed, expired, or carried no subject — 401. */
	TOKEN_REJECTED = 'token_rejected',
	/** The verified subject has no live TODAY `users` row — 403. */
	USER_NOT_PROVISIONED = 'user_not_provisioned',
	/** Inactive/soft-deleted user, suspended/deleted tenant, or more than one live row — 403. */
	PRINCIPAL_INACTIVE = 'principal_inactive',
	/** The IdP adapter could not be built, or failed for a reason other than rejecting the token — 503. */
	IDP_UNAVAILABLE = 'idp_unavailable',
	/** The identity lookup raised — 503. */
	RESOLUTION_FAILED = 'resolution_failed',
	/** A principal was attached but lacked a permission or role the route requires — 403. */
	PERMISSION_REFUSED = 'permission_refused',
	/** A principal required to have MFA has not enrolled; refused off the enrolment routes — 403 (TO-591). */
	MFA_ENROLMENT_REQUIRED = 'mfa_enrolment_required',
	/** A personal API key was presented and not admitted, for any reason — 401 (TO-516). */
	API_KEY_REJECTED = 'api_key_rejected',
	/** The access token was issued before the user's sessions were revoked — 401 (TO-64). */
	SESSION_REVOKED = 'session_revoked',
}
